import { useEffect, useMemo, useState, Fragment } from "react";
import { useLocation } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import { formatCurrency } from "@/lib/format";
import DrillDownModal from "@/components/DrillDownModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  CalendarIcon, TrendingDown, TrendingUp, Scale, BookOpen, FileSpreadsheet,
  CheckCircle2, AlertCircle, Download, Mail, Plus, Trash2, ArrowDownUp, Sparkles,
  CheckSquare, Square, ChevronDown, Building2, Tag, X, Users,
} from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { toast } from "sonner";
import { useEntity } from "@/context/EntityContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
// Extracted report subcomponents (iter 60d/e — Accounting.js was 2,188 lines).
import { EquityChangesReport } from "@/components/accounting/EquityChangesReport";
import { BudgetVsActualReport } from "@/components/accounting/BudgetVsActualReport";
import { ComparativePnlReport } from "@/components/accounting/ComparativePnlReport";
import { SalesByCustomerReport } from "@/components/accounting/SalesByCustomerReport";
import { ExpenseByVendorReport } from "@/components/accounting/ExpenseByVendorReport";
import { PnLReport } from "@/components/accounting/PnLReport";
import { BalanceSheetReport } from "@/components/accounting/BalanceSheetReport";
import { TrialBalanceReport } from "@/components/accounting/TrialBalanceReport";
import { GeneralLedgerReport } from "@/components/accounting/GeneralLedgerReport";
import { CashFlowReport } from "@/components/accounting/CashFlowReport";
// iter 72aj — AR/AP Aging + Cash Register.
import { ARDetailReport } from "@/components/accounting/ARDetailReport";
import { APDetailReport } from "@/components/accounting/APDetailReport";
import { CashRegisterReport } from "@/components/accounting/CashRegisterReport";
// iter 72al — AR/AP full flat ledger (all customers/vendors, all events).
import { LedgerReport } from "@/components/accounting/LedgerReport";

const toDate = (d) => (d instanceof Date ? d : new Date(d));
const fmtISO = (d) => format(toDate(d), "yyyy-MM-dd");

async function downloadStream(path, params, filename) {
  const res = await api.get(path, { params, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function DateField({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">{label}</div>
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" data-testid={testId} className="w-full justify-start font-normal h-9">
            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
            {value ? format(toDate(value), "MMM d, yyyy") : "Pick a date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar mode="single" selected={value ? toDate(value) : undefined} onSelect={(d) => d && onChange(d)} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function BalancedBadge({ balanced, difference, currency }) {
  return balanced ? (
    <span data-testid="balanced-badge" className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF4EE] text-[#2F6B4A] px-3 py-1 text-xs">
      <CheckCircle2 className="w-3.5 h-3.5" /> Books balance
    </span>
  ) : (
    <span data-testid="unbalanced-badge" className="inline-flex items-center gap-1.5 rounded-full bg-[#FCE7E9] text-[#B4283A] px-3 py-1 text-xs">
      <AlertCircle className="w-3.5 h-3.5" /> Out by {formatCurrency(Math.abs(difference || 0), currency)}
    </span>
  );
}

function CsvButton({ onClick, testId }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      data-testid={testId}
      onClick={onClick}
      className="h-9 gap-2"
    >
      <Download className="w-3.5 h-3.5" /> CSV
    </Button>
  );
}

function DeltaBadge({ delta, pct, currency, inverse = false }) {
  if (delta === 0 || delta == null) {
    return <span className="text-xs text-[var(--muted-text)] font-mono-data">—</span>;
  }
  const good = inverse ? delta < 0 : delta > 0;
  return (
    <span className={`text-xs font-mono-data ${good ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
      {delta > 0 ? "+" : ""}{formatCurrency(delta, currency)}
      {pct != null && ` (${pct > 0 ? "+" : ""}${pct.toFixed(1)}%)`}
    </span>
  );
}

// ---------------- Monthly Close card ----------------
function MonthlyCloseCard({ currency, userEmail }) {
  const now = new Date();
  const [month, setMonth] = useState(format(now, "yyyy-MM"));
  const [emailing, setEmailing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const openPreview = async () => {
    const { data } = await api.get("/accounting/monthly-close", { params: { month } });
    setPreview(data);
    setPreviewOpen(true);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await downloadStream("/accounting/monthly-close.pdf", { month }, `ledger-monthly-close-${month}.pdf`);
      toast.success("Monthly close PDF downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate PDF");
    } finally { setDownloading(false); }
  };

  const emailPdf = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/accounting/monthly-close/email", { month });
      toast.success(`Sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    } finally { setEmailing(false); }
  };

  // 12 recent months
  const months = [];
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ value: format(d, "yyyy-MM"), label: format(d, "MMMM yyyy") });
  }

  return (
    <div
      data-testid="monthly-close-card"
      className="card-flat p-5 mb-8 border-l-4 border-l-[#E07A5F]"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-md bg-[#E07A5F]/15 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-[#E07A5F]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Monthly close</div>
            <div className="font-display text-xl mt-0.5">Close the books for a month</div>
            <div className="text-xs text-[var(--muted-text)] mt-1">
              One PDF with P&amp;L, Balance Sheet variance vs prior month, and Cash Flow — download or email it to yourself.
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="mc-month-select" className="w-[180px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            data-testid="mc-preview-btn"
            onClick={openPreview}
            className="h-9"
          >
            Preview
          </Button>
          <Button
            variant="outline"
            data-testid="mc-download-btn"
            onClick={downloadPdf}
            disabled={downloading}
            className="h-9 gap-2"
          >
            <Download className="w-3.5 h-3.5" /> PDF
          </Button>
          <Button
            data-testid="mc-email-btn"
            onClick={emailPdf}
            disabled={emailing}
            className="h-9 gap-2 bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
          >
            <Mail className="w-3.5 h-3.5" /> Email me
          </Button>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {preview?.period.month} — Monthly close preview
            </DialogTitle>
            <DialogDescription>
              Snapshot compared to {preview?.previous_period.month}. Emailing the close attaches the full PDF (P&amp;L + Balance Sheet + Cash Flow).
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {["income", "expenses", "net_income"].map((k) => {
                  const v = preview.pnl.variance[k];
                  const labels = { income: "Revenue", expenses: "Expenses", net_income: "Net income" };
                  return (
                    <div key={k} className="card-flat p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">{labels[k]}</div>
                      <div className="font-mono-data text-lg mt-1">{formatCurrency(v.current, currency)}</div>
                      <DeltaBadge delta={v.delta} pct={v.pct} currency={currency} inverse={k === "expenses"} />
                    </div>
                  );
                })}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-2">Balance sheet vs {preview.previous_period.month}</div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-[var(--muted-text)] uppercase">
                    <tr>
                      <th className="text-left py-1">Section</th>
                      <th className="text-right py-1">This month-end</th>
                      <th className="text-right py-1">Prev end</th>
                      <th className="text-right py-1">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[["assets", "Assets"], ["liabilities", "Liabilities"], ["equity", "Equity"]].map(([k, label]) => {
                      const v = preview.balance_sheet.variance[k];
                      return (
                        <tr key={k} className="border-t border-[var(--divider)]">
                          <td className="py-1.5">{label}</td>
                          <td className="py-1.5 text-right font-mono-data">{formatCurrency(v.current, currency)}</td>
                          <td className="py-1.5 text-right font-mono-data text-[var(--muted-text)]">{formatCurrency(v.previous, currency)}</td>
                          <td className="py-1.5 text-right"><DeltaBadge delta={v.delta} pct={v.pct} currency={currency} inverse={k === "liabilities"} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-[var(--muted-text)]">
                Emails go to <b>{userEmail}</b>. Full report with cash-flow section is in the attached PDF.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Profit & Loss ----------------


// ---------------- Balance Sheet ----------------

// ---------------- Trial Balance ----------------

// ---------------- Page shell ----------------
export default function Accounting() {
  const { user } = useAuth();
  const { currentEntity } = useEntity();
  const currency = user?.currency || "USD";
  const isBusiness = currentEntity?.workspace_type === "business";
  // iter 72al — read initial tab from ?tab= so sidebar deep-links (e.g.
  // /accounting?tab=ar-ledger) land on the right report. Re-evaluated on
  // every location change so switching from ar-ledger → ap-aging via the
  // sidebar (same route, different query) actually moves the tab.
  const location = useLocation();
  const urlTab = useMemo(() => {
    return new URLSearchParams(location.search).get("tab") || "pnl";
  }, [location.search]);
  const [tab, setTab] = useState(urlTab);
  useEffect(() => { setTab(urlTab); }, [urlTab]);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (tab && tab !== "pnl") url.searchParams.set("tab", tab);
    else url.searchParams.delete("tab");
    window.history.replaceState({}, "", url.toString());
  }, [tab]);

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Books</div>
        <h1 className="font-display text-4xl mt-2">Accounting</h1>
        <p className="text-sm text-[var(--muted-text)] mt-1">
          Full financial statements derived from every transaction — P&amp;L, Balance Sheet, Trial Balance, General Ledger, and Cash Flow. Close your books each month with a single PDF.
        </p>
      </div>

      <MonthlyCloseCard currency={currency} userEmail={user?.email} />

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList data-testid="accounting-tabs" className="bg-[var(--hover)] flex-wrap h-auto">
          <TabsTrigger value="pnl" data-testid="tab-pnl" className="gap-2">
            <FileSpreadsheet className="w-3.5 h-3.5" /> P&amp;L
          </TabsTrigger>
          <TabsTrigger value="bs" data-testid="tab-bs" className="gap-2">
            <Scale className="w-3.5 h-3.5" /> Balance Sheet
          </TabsTrigger>
          <TabsTrigger value="tb" data-testid="tab-tb" className="gap-2">
            <BookOpen className="w-3.5 h-3.5" /> Trial Balance
          </TabsTrigger>
          <TabsTrigger value="gl" data-testid="tab-gl" className="gap-2">
            <BookOpen className="w-3.5 h-3.5" /> General Ledger
          </TabsTrigger>
          <TabsTrigger value="cf" data-testid="tab-cf" className="gap-2">
            <ArrowDownUp className="w-3.5 h-3.5" /> Cash Flow
          </TabsTrigger>
          <TabsTrigger value="eq" data-testid="tab-equity" className="gap-2">
            <Scale className="w-3.5 h-3.5" /> Equity
          </TabsTrigger>
          <TabsTrigger value="bva" data-testid="tab-bva" className="gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Budget vs Actual
          </TabsTrigger>
          <TabsTrigger value="cmp" data-testid="tab-comparative" className="gap-2">
            <ArrowDownUp className="w-3.5 h-3.5" /> Comparative P&L
          </TabsTrigger>
          <TabsTrigger value="sbc" data-testid="tab-sales-by-customer" className="gap-2">
            <Users className="w-3.5 h-3.5" /> Sales by Customer
          </TabsTrigger>
          <TabsTrigger value="ebv" data-testid="tab-expense-by-vendor" className="gap-2">
            <Building2 className="w-3.5 h-3.5" /> Expense by Vendor
          </TabsTrigger>
          {/* iter 72aj — Business-only aging + cash register. Hidden in
              Personal workspaces because there's no AR/AP/CoA there. */}
          {isBusiness && (
            <>
              <TabsTrigger value="ar-aging" data-testid="tab-ar-detail" className="gap-2">
                <Users className="w-3.5 h-3.5" /> AR Aging
              </TabsTrigger>
              <TabsTrigger value="ap-aging" data-testid="tab-ap-detail" className="gap-2">
                <Building2 className="w-3.5 h-3.5" /> AP Aging
              </TabsTrigger>
              {/* iter 72al — full flat AR/AP ledger (all events, all parties). */}
              <TabsTrigger value="ar-ledger" data-testid="tab-ar-ledger" className="gap-2">
                <BookOpen className="w-3.5 h-3.5" /> AR Ledger
              </TabsTrigger>
              <TabsTrigger value="ap-ledger" data-testid="tab-ap-ledger" className="gap-2">
                <BookOpen className="w-3.5 h-3.5" /> AP Ledger
              </TabsTrigger>
              <TabsTrigger value="cash-reg" data-testid="tab-cash-register" className="gap-2">
                <ArrowDownUp className="w-3.5 h-3.5" /> Cash Register
              </TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="pnl"><PnLReport currency={currency} /></TabsContent>
        <TabsContent value="bs"><BalanceSheetReport currency={currency} /></TabsContent>
        <TabsContent value="tb"><TrialBalanceReport currency={currency} /></TabsContent>
        <TabsContent value="gl"><GeneralLedgerReport currency={currency} /></TabsContent>
        <TabsContent value="cf"><CashFlowReport currency={currency} /></TabsContent>
        <TabsContent value="eq"><EquityChangesReport currency={currency} /></TabsContent>
        <TabsContent value="bva"><BudgetVsActualReport currency={currency} /></TabsContent>
        <TabsContent value="cmp"><ComparativePnlReport currency={currency} /></TabsContent>
        <TabsContent value="sbc"><SalesByCustomerReport currency={currency} /></TabsContent>
        <TabsContent value="ebv"><ExpenseByVendorReport currency={currency} /></TabsContent>
        {isBusiness && (
          <>
            <TabsContent value="ar-aging"><ARDetailReport currency={currency} /></TabsContent>
            <TabsContent value="ap-aging"><APDetailReport currency={currency} /></TabsContent>
            <TabsContent value="ar-ledger"><LedgerReport kind="ar" currency={currency} /></TabsContent>
            <TabsContent value="ap-ledger"><LedgerReport kind="ap" currency={currency} /></TabsContent>
            <TabsContent value="cash-reg"><CashRegisterReport currency={currency} /></TabsContent>
          </>
        )}
      </Tabs>
    </AppShell>
  );
}


